import { Controller } from "@hotwired/stimulus"

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 150
let sequence = 0

export default class extends Controller {
  static values = { url: String }

  connect() {
    // Every input gets its own copy of the shared datalist, so a search in one
    // field does not change the suggestions of the others
    const shared = document.getElementById(this.element.getAttribute("list"))
    this.datalist = shared.cloneNode(true)
    this.datalist.id = `${shared.id}-${++sequence}`
    this.element.setAttribute("list", this.datalist.id)
    this.element.insertAdjacentElement("afterend", this.datalist)

    this.staticOptions = Array.from(this.datalist.children)
    this.pinnedOptions = this.staticOptions.filter(option => option.value.trim() === "ALL")
  }

  disconnect() {
    clearTimeout(this.timer)
    this.request?.abort()
    this.datalist.remove()
  }

  search() {
    const query = this.query()
    clearTimeout(this.timer)
    this.request?.abort()

    if (query.length < MIN_QUERY_LENGTH) {
      this.datalist.replaceChildren(...this.sorted(query))
      return
    }

    this.timer = setTimeout(() => this.fetch(query), DEBOUNCE_MS)
  }

  trim() {
    this.element.value = this.element.value.trim()
  }

  async fetch(query) {
    this.request = new AbortController()
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query)

    let results
    try {
      const response = await fetch(url, { signal: this.request.signal, headers: { Accept: "application/json" } })
      if (!response.ok) return
      results = await response.json()
    } catch (error) {
      if (error.name === "AbortError") return
      throw error
    }
    if (this.query() !== query) return

    const options = results.map(([value, label]) => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = label
      return option
    })
    this.datalist.replaceChildren(...this.pinnedOptions, ...options)
  }

  query() {
    return this.element.value.trim().toUpperCase()
  }

  sorted(query) {
    if (!query) return this.staticOptions

    return [...this.staticOptions].sort((a, b) => this.rank(a, query) - this.rank(b, query))
  }

  rank(option, query) {
    const value = option.value.trim().toUpperCase()
    const label = option.textContent.trim().toUpperCase()

    if (value === query) return 0
    if (value.startsWith(query)) return 1
    if (value.includes(query)) return 2
    if (label === query) return 3
    if (label.startsWith(query)) return 4
    if (label.includes(query)) return 5
    return 6
  }
}
